import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import Modal from 'components/Modal';
import styled, { css } from 'styled-components';
import { Col, Row } from 'antd';
import appointmentSelectors from 'features/appointment/services/selectors';
import { formatCurrency } from 'utils/formatCurrency';
import Box from 'components/Box';
import { IServiceItemData } from 'services/shop/types/categories';

export type OpenSelectMultipleServicesModalProps = {
    selectedCategoryId: string;
    selectedServiceIds: string[];
    baseServices?: IServiceItemData[]; // this case is for services that dont have in categoriesBase
}

type SelectMultipleServicesModalRef = {
    open: (data: OpenSelectMultipleServicesModalProps) => Promise<IServiceItemData[] | null>;
}

export const useSelectMultipleServicesModalRef = () => useRef<SelectMultipleServicesModalRef>(null);

const SelectMultipleServicesModal = forwardRef<SelectMultipleServicesModalRef>((_, ref) => {
    const [visible, setVisible] = useState(false);
    const [selectedCategoryId, setSelectedCategoryId] = useState('');
    const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
    const [baseServices, setBaseServices] = useState<IServiceItemData[]>([]);
    const callBackRef = useRef<((data: IServiceItemData[] | null) => void) | null>(null);

    const categoriesBase = appointmentSelectors.getCategories();
    const servicesFilterByCategory = useMemo(() => {
        return categoriesBase.find((category) => category.id === selectedCategoryId)?.services;
    }, [categoriesBase, selectedCategoryId]);

    const handleClose = () => {
        setVisible(false);
        callBackRef?.current?.(null);
        setBaseServices([]);
        setSelectedCategoryId('');
        setSelectedServiceIds([]);
    };

    const onSubmit = () => {
        setVisible(false);
        const categoriesBaseFlatmap = categoriesBase?.flatMap((category) => category.services) ?? [];
        const servicesMap = new Map(
            [...categoriesBaseFlatmap, ...baseServices].map(service => [service.id, service])
        );
        const selectedServices = selectedServiceIds
            .map(id => servicesMap.get(id))
            .filter((service): service is IServiceItemData => service !== undefined);
        callBackRef?.current?.(selectedServices);
    };

    useImperativeHandle(ref, () => ({
        open: async (data: OpenSelectMultipleServicesModalProps) => {
            setVisible(true);
            if (data.selectedCategoryId) {
                setSelectedCategoryId(data.selectedCategoryId);
            } else {
                setSelectedCategoryId(categoriesBase?.[0]?.id || '');
            }
            if (data.selectedServiceIds) {
                setSelectedServiceIds(data.selectedServiceIds);
            }
            if (data.baseServices) {
                setBaseServices(data.baseServices);
            }
            return new Promise<IServiceItemData[] | null>((resolve) => {
                callBackRef.current = resolve;
            });
        }
    }));

    const onCLickCategory = (categoryId: string) => {
        setSelectedCategoryId(categoryId);
    };
    const onCLickService = (serviceId: string) => {
        if (selectedServiceIds.includes(serviceId)) {
            setSelectedServiceIds(selectedServiceIds.filter((id) => id !== serviceId));
        } else {
            setSelectedServiceIds([...selectedServiceIds, serviceId]);
        }
    };

    if (!visible) return null;
    return (
        <Modal
            modalTitle={'select services'}
            visible={visible}
            onClose={handleClose}
            onSubmit={onSubmit}
            width={'1600px'}
            noneBodyStyle={true}
            noneFooterStyle={true}
            footerPadding={0.5}
            okTitle='OK'
            v2={true}
        >
            <Container>
                <CateGoryContainer>
                    {categoriesBase.map((category) => (
                        <CategoryItem
                            key={category.id}
                            selected={selectedCategoryId === category.id}
                            onClick={() => onCLickCategory(category.id)}
                        >
                            <div className='text'>{category.categoryName}</div>
                        </CategoryItem>
                    ))}
                </CateGoryContainer>
                <ServicesContainer>
                    <Row gutter={[16, 8]}>
                        {servicesFilterByCategory?.map((service) => {
                            const selected = selectedServiceIds.includes(service.id);
                            return (
                                <Col xs={24} sm={24} md={24} lg={8} xl={8} key={service.id}>
                                    <ServiceItem selected={selected} onClick={() => onCLickService(service.id)}>
                                        <Box display='flex' flexDirection='column' gap='1'>
                                            <div className='name'>{service.serviceName}</div>
                                            <div className='price'>{formatCurrency(service.priceSell)}</div>
                                        </Box>
                                        {selected && <CheckIconBox><IcCheckService /></CheckIconBox>}
                                    </ServiceItem>
                                </Col>
                            );
                        })}
                    </Row>
                </ServicesContainer>
            </Container>
        </Modal>
    );
});

SelectMultipleServicesModal.displayName = 'SelectMultipleServicesModal';
export default SelectMultipleServicesModal;


const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CateGoryContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(175px, 1fr));
  padding: 1rem;
`;

const CategoryItem = styled.div<{ selected: boolean }>`
  display: flex;
  padding: 25px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
  align-self: stretch;
  color: #505050;
  text-align: center;
  font-family: Poppins;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 30px;
  text-transform: capitalize;
  background: #fcfcfd;
  box-shadow: 0 2px 4px 0 rgba(44, 35, 66, 0.4), 0 4px 8px -3px rgba(45, 35, 66, 0.3),
    0 -3px 0 0 #d6d6e7 inset;
  cursor: pointer;

  ${({ selected }) =>
        selected &&
        css`
      color: #fff;
      background: #2d6772;
      box-shadow: 0 2px 4px 0 rgba(44, 35, 66, 0.4), 0 4px 8px -3px rgba(45, 35, 66, 0.3),
        0 -3px 0 0 rgba(8, 56, 65, 0.5) inset;
    `}
  .text {
    font-size: 18px;
    font-weight: 600;
    line-height: 30px;
    text-transform: capitalize;
    ${({ selected }) =>
        selected &&
        css`
        color: #fff;
      `}
  }
`;

const ServicesContainer = styled.div`
  height: 700px;
  overflow-y: auto;
  padding: 0 1rem;
`;

const ServiceItem = styled.div<{ selected: boolean }>`
  cursor: pointer;
  padding: 0.5rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  border-radius: 5px;
  background: #e5e6eb;
  box-sizing: border-box;
  border: 2px solid transparent;
  box-shadow: 0 -4px 4px 0 rgba(0, 0, 0, 0.25) inset, 0 8px 4px 3px rgba(255, 255, 255, 0.25) inset;
  transition: all 0.3s ease;
  ${({ selected }) =>
        selected &&
        css`
      transition: all 0.3s ease;
      border-color: #d2464f;
    `}

  .name {
    flex: 1;
    overflow: hidden;
    color: #1d2129;
    text-overflow: ellipsis;
    font-size: 18px;
    font-weight: 500;
    line-height: normal;
  }
  .price {
    color: #1d2129;
    font-size: 18px;
    font-weight: 600;
    text-transform: capitalize;
  }
`;

const CheckIconBox = styled.div`
    display: flex;
    width: 32px;
    height: 32px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
    border-radius: 100px;
    background: #D2464F;
`;

const IcCheckService = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M9.20554 19.3762C8.81194 19.3762 8.43803 19.2116 8.16252 18.9234L2.59307 13.0986C2.02236 12.5017 2.02236 11.5137 2.59307 10.9169C3.16378 10.32 4.10841 10.32 4.67912 10.9169L9.20554 15.6508L19.3209 5.07145C19.8916 4.47456 20.8362 4.47456 21.4069 5.07145C21.9776 5.66834 21.9776 6.65629 21.4069 7.25318L10.2486 18.9234C9.97305 19.2116 9.59913 19.3762 9.20554 19.3762Z" fill="white" />
    </svg>);